import { useState, useRef, useEffect } from "react";
import { t } from "@/lib/i18n";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, PaperPlaneTilt, Trash, Robot, Microphone, Paperclip,
  PaperPlaneRight, ArrowCounterClockwise, X, UserCircle, Sparkle, FloppyDisk
} from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { MemoryChip } from "@/components/MemoryChip";
import { RecipientInput } from "@/components/RecipientInput";
import { useSpeech } from "@/hooks/useSpeech";
import { useAuth } from "@/App";

const parseRecipients = (s) => String(s || "").split(/[,;]+/).map((x) => x.trim()).filter(Boolean);

export default function ComposePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get("draft_id");
  const mailbox = searchParams.get("mailbox") || "";
  const mbParams = mailbox ? { params: { mailbox } } : {};
  const { user } = useAuth();
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendAt, setSendAt] = useState(null);
  const [draftThreadId, setDraftThreadId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const { listening, toggleMic } = useSpeech((final) => setInput((prev) => (prev + " " + final).trim()));
  const [flash, setFlash] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, thinking]);

  useEffect(() => {
    if (draftId) {
      api.get(`/drafts/${draftId}`, mbParams).then(({ data }) => {
        setTo(parseRecipients(data.to_email));
        setCc(data.cc || []); setBcc(data.bcc || []);
        if ((data.cc || []).length) setShowCc(true);
        if ((data.bcc || []).length) setShowBcc(true);
        setSubject(data.subject || ""); setBody(data.body || "");
        setSendAt(data.send_at || null); setDraftThreadId(data.thread_id || null);
      }).catch(() => { toast.error(t("compose.draft_notfound", "Draft not found")); navigate("/app"); });
    } else if (location.state?.forward) {
      setSubject(location.state.forward.subject || "");
      setBody(location.state.forward.body || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  const saveAsDraft = async () => {
    setSaving(true);
    try {
      const payload = { to_email: to.join(", "), cc, bcc, subject, body };
      if (draftId) await api.put(`/drafts/${draftId}`, payload, mbParams);
      else await api.post("/drafts", payload, mbParams);
      toast.success(t("compose.draft_saved", "Saved to Drafts"));
      navigate("/app");
    } catch (err) { toast.error(errDetail(err)); }
    setSaving(false);
  };

  const cancelSchedule = async () => {
    try { await api.post(`/drafts/${draftId}/cancel-schedule`); setSendAt(null); toast.success(t("thread.schedule_cancelled", "Scheduled send cancelled")); }
    catch (err) { toast.error(errDetail(err)); }
  };

  const discard = async () => {
    if (draftId) { try { await api.delete(`/drafts/${draftId}`); toast.success(t("compose.draft_discarded", "Draft discarded")); } catch { /* noop */ } }
    navigate("/app");
  };

  const askAgent = async (e) => {
    e?.preventDefault();
    const instruction = input.trim();
    if (!instruction || thinking) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: instruction, attachments: attachments.map((f) => f.name) }]);
    setThinking(true);
    try {
      const { data } = await api.post("/agent/compose", {
        instruction, to_email: to.join(", "), subject, body, attachments: attachments.map((f) => f.name),
      });
      setUndoStack((s) => [...s, { to, subject, body }]);
      if (data.to_email !== undefined && data.to_email !== null) setTo(parseRecipients(data.to_email));
      setSubject(data.subject ?? subject);
      setBody(data.body ?? body);
      setFlash(true); setTimeout(() => setFlash(false), 900);
      setMsgs((m) => [...m, { role: "agent", text: data.message, contact: data.contact_matched, memories_used: data.memories_used }]);
    } catch (err) { toast.error(errDetail(err)); }
    setThinking(false);
  };

  const undo = () => {
    const prev = undoStack[undoStack.length - 1];
    if (!prev) return;
    setTo(prev.to); setSubject(prev.subject); setBody(prev.body);
    setUndoStack((s) => s.slice(0, -1));
    setMsgs((m) => [...m, { role: "agent", text: "Reverted to the previous draft." }]);
  };

  const uploadFiles = async (fileList) => {
    setUploading(true);
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
        toast.success(`Uploaded ${data.name}`);
      } catch (e) { toast.error(`Upload failed for ${f.name}: ${errDetail(e)}`); }
    }
    setUploading(false);
  };

  const send = async () => {
    if (to.length === 0 || !subject || !body) { toast.error(t("compose.fill_error", "Fill in To, Subject and Body (or ask the agent to draft them)")); return; }
    setSending(true);
    try {
      await api.post("/emails/send", { to_email: to.join(", "), cc, bcc, subject, body, thread_id: draftThreadId, attachment_ids: attachments.map((a) => a.id) }, mbParams);
      if (draftId) { try { await api.delete(`/drafts/${draftId}`); } catch { /* noop */ } }
      toast.success(t("compose.sent", "Email sent"));
      navigate("/app");
    } catch (err) { toast.error(errDetail(err)); }
    setSending(false);
  };

  const fieldCls = `w-full border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${flash ? "border-blue-400 bg-blue-50/50" : "border-gray-300"}`;

  return (
    <div className="h-screen flex overflow-hidden" data-testid="compose-page">
      {/* Left: draft form */}
      <div className="flex-1 min-w-0 flex flex-col bg-white border-r border-gray-200">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button data-testid="compose-back-btn" onClick={() => navigate("/app")}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft size={16} /> Inbox
            </button>
            <h1 className="font-heading font-bold text-gray-900">{draftId ? t("compose.edit_draft", "Edit draft") : t("compose.new_message", "New message")}</h1>
            {sendAt && (
              <span data-testid="compose-scheduled-badge" className="text-[11px] font-semibold bg-amber-100 text-amber-700 rounded px-2 py-0.5 inline-flex items-center gap-1.5">
                ⏰ {t("thread.scheduled", "Scheduled")} · {new Date(sendAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                <button data-testid="compose-cancel-schedule-btn" onClick={cancelSchedule} className="underline hover:text-amber-900">{t("thread.cancel", "Cancel")}</button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {undoStack.length > 0 && (
              <button data-testid="compose-undo-btn" onClick={undo}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 transition-colors">
                <ArrowCounterClockwise size={14} /> Undo
              </button>
            )}
            <button data-testid="compose-save-draft-btn" onClick={saveAsDraft} disabled={saving}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 hover:bg-gray-50 rounded-md px-3 py-1.5 transition-colors disabled:opacity-50">
              <FloppyDisk size={14} /> {saving ? t("compose.saving", "Saving…") : t("compose.save_draft", "Save as Draft")}
            </button>
            <button data-testid="compose-discard-btn" onClick={discard}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 rounded-md px-3 py-1.5 transition-colors">
              <Trash size={14} /> Discard
            </button>
            <button data-testid="compose-send-btn" onClick={send} disabled={sending}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2 transition-colors disabled:opacity-50">
              <PaperPlaneTilt size={15} /> {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-6 max-w-3xl w-full">
          <RecipientInput label="To" value={to} onChange={setTo} testId="compose-to-input" placeholder="recipient@company.com" />
          <div className="flex gap-3 -mt-3 mb-3">
            {!showCc && <button data-testid="compose-cc-toggle" onClick={() => setShowCc(true)} className="text-xs text-blue-600 hover:underline">Cc</button>}
            {!showBcc && <button data-testid="compose-bcc-toggle" onClick={() => setShowBcc(true)} className="text-xs text-blue-600 hover:underline">Bcc</button>}
          </div>
          {showCc && <RecipientInput label="Cc" value={cc} onChange={setCc} testId="compose-cc-input" placeholder="cc@company.com" />}
          {showBcc && <RecipientInput label="Bcc" value={bcc} onChange={setBcc} testId="compose-bcc-input" placeholder="bcc@company.com" />}
          <label className="text-xs font-medium text-gray-500 block mb-1">{t("compose.subject", "Subject")}</label>
          <input data-testid="compose-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder={t("compose.subject", "Subject")} className={`${fieldCls} mb-4`} />
          <label className="text-xs font-medium text-gray-500 block mb-1">Message</label>
          <textarea data-testid="compose-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={16}
            placeholder={t("compose.write_placeholder", "Write your message — or just tell the agent what you want to say →")}
            className={`${fieldCls} resize-none leading-relaxed`} />
        </div>
      </div>

      {/* Right: compose agent */}
      <div className="w-[380px] shrink-0 h-full flex flex-col bg-slate-50">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-white">
          <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[9px] font-bold">AI</span>
          <span className="text-sm font-semibold text-gray-800">{t("compose.agent_title", "Compose Agent")}</span>
          <Sparkle size={13} className="text-blue-500" />
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-thin p-3 space-y-2.5">
          {msgs.length === 0 && (
            <div data-testid="compose-agent-empty" className="text-xs text-gray-400 bg-white border border-gray-200 rounded-md p-3 leading-relaxed">
              <span className="font-semibold text-gray-600 block mb-1">{t("compose.speak_or_type", "Speak or type what you want to say:")}</span>
              "Write an email to Elena about rescheduling our Series A call" · "Make it shorter" · "More formal" · "Add that I'll share the deck by Friday"
              <span className="block mt-2 text-gray-500">Use the <Microphone size={11} className="inline" /> mic for voice — the draft fills itself in on the left.</span>
            </div>
          )}
          {msgs.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="flex justify-end fade-up">
                <div data-testid="compose-msg-user" className="max-w-[85%] bg-blue-600 text-white rounded-lg rounded-br-sm px-3 py-2 text-[13px] leading-relaxed">
                  {m.text}
                  {m.attachments?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {m.attachments.map((a, j) => (
                        <span key={j} className="flex items-center gap-1 bg-blue-500 rounded px-1.5 py-0.5 text-[11px]"><Paperclip size={10} />{a}</span>
                      ))}
                    </div>
                  )}
                </div>
                <UserCircle size={18} className="text-blue-400 ml-1.5 mt-1 shrink-0" />
              </div>
            ) : (
              <div key={i} className="flex fade-up">
                <span className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center mr-1.5 mt-1 shrink-0 text-[8px] font-bold text-blue-700">AI</span>
                <div data-testid="compose-msg-agent" className="max-w-[85%] bg-white border border-gray-200 text-gray-800 rounded-lg px-3 py-2 text-[13px] leading-relaxed">
                  {m.text}
                  {m.contact && <div className="text-[11px] text-blue-600 mt-1">Matched contact: {m.contact}</div>}
                  <MemoryChip memories={m.memories_used} />
                </div>
              </div>
            )
          ))}
          {thinking && (
            <div className="flex items-center gap-1.5 text-xs text-gray-400 pl-7" data-testid="compose-agent-thinking">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:120ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:240ms]" />
              drafting…
            </div>
          )}
        </div>

        <div className="p-3 bg-white border-t border-gray-200">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {attachments.map((f, i) => (
                <span key={i} data-testid={`compose-attachment-chip-${i}`} className="flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-700">
                  <Paperclip size={11} /> {f.name} <span className="text-gray-400">({Math.max(1, Math.round((f.size || 0) / 1024))} KB)</span>
                  <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}
          <form onSubmit={askAgent} className="flex items-end gap-1.5 border border-gray-300 rounded-lg px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500">
            <button type="button" data-testid="compose-agent-attach" onClick={() => fileRef.current?.click()}
              className="p-1 text-gray-400 hover:text-gray-700 transition-colors" title={t("common.attach_files", "Attach files")}>
              <Paperclip size={17} />
            </button>
            <input ref={fileRef} type="file" multiple className="hidden" data-testid="compose-agent-file-input"
              onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
            {uploading && <span data-testid="compose-uploading" className="text-xs text-gray-400 self-center">uploading…</span>}
            <textarea data-testid="compose-agent-input" rows={1} value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); askAgent(); } }}
              placeholder={listening ? "Listening…" : "Tell me what to write…"}
              className="flex-1 resize-none text-sm py-1 focus:outline-none max-h-28" />
            <button type="button" data-testid="compose-agent-mic" onClick={toggleMic} title={t("common.speech_to_text", "Speech to text")}
              className={`p-1 transition-colors ${listening ? "mic-active" : "text-gray-400 hover:text-gray-700"}`}>
              <Microphone size={17} weight={listening ? "fill" : "regular"} />
            </button>
            <button type="submit" data-testid="compose-agent-send" disabled={thinking || !input.trim()}
              className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-40">
              <PaperPlaneRight size={15} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
